import { ViewportScroller } from '@angular/common';
import {
    EnvironmentProviders,
    inject,
    provideEnvironmentInitializer,
} from '@angular/core';
import { NavigationEnd, Router, Scroll } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Scroll handling for the whole app, replacing `scrollPositionRestoration: 'enabled'`.
 *
 * `'enabled'` scrolls to the top on EVERY navigation — including one that only
 * rewrites the query string. Screens that keep their state in the URL (analytics
 * filters and tabs, the message-quota tabs) navigate on every control change, so the
 * page jumped to the top whenever a dropdown changed.
 *
 * Here: back / forward restores the stored position (as before), a navigation to a
 * new path scrolls to the top (as before), and a navigation that changes only the
 * query string or fragment keeps the scroll where it is.
 *
 * Requires `withInMemoryScrolling({ scrollPositionRestoration: 'disabled' })`: the
 * router still records positions and emits `Scroll` events (after the view has
 * rendered), it just no longer acts on them itself.
 */
export function provideQueryAwareScrolling(): EnvironmentProviders {
    return provideEnvironmentInitializer(() => {
        const router = inject(Router);
        const viewportScroller = inject(ViewportScroller);

        // With 'disabled' the router leaves the browser's own restoration on, which
        // would race this handler on back / forward.
        viewportScroller.setHistoryScrollRestoration('manual');

        let lastPath: string | null = null;

        router.events
            .pipe(filter((event): event is Scroll => event instanceof Scroll))
            .subscribe((event) => {
                const url =
                    event.routerEvent instanceof NavigationEnd
                        ? event.routerEvent.urlAfterRedirects
                        : event.routerEvent.url;
                const path = pathOf(url);
                const samePath = path === lastPath;
                lastPath = path;

                if (event.position) {
                    viewportScroller.scrollToPosition(event.position);
                    return;
                }
                if (!samePath) {
                    viewportScroller.scrollToPosition([0, 0]);
                }
            });
    });
}

/** The URL without its query string and fragment. */
function pathOf(url: string): string {
    return url.split(/[?#]/)[0];
}
